import { Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CreateCarForm } from "@/components/forms";
import { useCreateCar } from "@/services/cars/create-car";
import { useGarageCars } from "@/services/cars/get-garage-cars";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { showCustomNotification } from "@/utils/notifications";

import type { CreateCarInput } from "@/services/cars/create-car";
import type { Car } from "@/types/car";
import type { List } from "@/types/entity";
import type { Garage } from "@/types/garage";
import type { MantineStyleProps } from "@mantine/core";
import type { Dispatch, FC, SetStateAction } from "react";

export type CreateCarProps = {
  autoCloseDelay?: boolean | number;
  garageId: string | undefined;
  garages?: List<Garage> | undefined;
  onClose?: () => void;
  onSuccess?: (carId?: string | number) => void;
  setSelectedGarageForCarCreation?: Dispatch<SetStateAction<string | undefined>>;
  showCarId?: boolean;
  showLessElements?: boolean;
} & MantineStyleProps;

export const CreateCar: FC<CreateCarProps> = ({
  autoCloseDelay = false,
  garageId,
  garages,
  onClose,
  onSuccess,
  setSelectedGarageForCarCreation,
  showCarId,
  showLessElements = false,
  ...props
}) => {
  const { t } = useTranslation();

  const { data: plans } = usePlansUser();
  const { data: garageCars } = useGarageCars({ garageId, page: 1, pageSize: 999999 });

  const { mutate, isPending } = useCreateCar({
    mutationConfig: {
      onSuccess: (createdCar) => handleSuccess(createdCar),
      onError: handleError,
    },
  });

  function handleSuccess(createdCar: Car) {
    const { hide } = showCustomNotification({
      title: t("components.CreateCar.success.title"),
      message: t("components.CreateCar.success.message"),
      actionButton: onSuccess
        ? {
            text: t("components.CreateCar.success.action"),
            action: () => {
              showCarId ? onSuccess(createdCar.identifier) : onSuccess();

              hide();
            },
          }
        : undefined,
      autoClose: autoCloseDelay,
    });
    onClose?.();
  }

  function handleError() {
    showCustomNotification({
      title: t("components.CreateCar.error.title"),
      message: t("components.CreateCar.error.message"),
      autoClose: autoCloseDelay,
    });
  }

  function handleSubmit(values: CreateCarInput) {
    // validation based on plan limits
    const assetsLimit = plans?.config?.cars_per_garage;
    const currentCount = garageCars?.results?.length ?? 0;

    if (assetsLimit && Number(assetsLimit) > 0 && currentCount >= Number(assetsLimit)) {
      showCustomNotification({
        title: t("plans.contentLocker.limitPlanTitle", { planName: plans?.name ?? "" }),
        message: t("plans.limitReached"),
        autoClose: autoCloseDelay,
      });
      return;
    }

    mutate({
      data: values,
      garageId,
    });
  }

  return (
    <Stack {...props}>
      <CreateCarForm
        loading={isPending}
        garageId={garageId}
        garages={garages}
        onCancel={onClose}
        onSubmit={handleSubmit}
        {...(setSelectedGarageForCarCreation && {
          setSelectedGarageForCarCreation,
        })}
        showLessElements={showLessElements}
      />
    </Stack>
  );
};
